import React, { useState } from 'react';
import {
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentArrowDownIcon,
  PaperAirplaneIcon,
  EyeIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { templateService } from '../services/api';
import { toast } from 'react-hot-toast';
import { useLanguage } from '../contexts/LanguageContext';

const MetaTemplateList = ({ templates, loading, onBroadcast, onRefresh, onTemplateClick }) => {
  const { t } = useLanguage();
  const [resendingTemplateId, setResendingTemplateId] = useState(null);

  const getStatusIcon = (status) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED':
        return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'REJECTED':
        return <XCircleIcon className="w-5 h-5 text-red-500" />;
      case 'PENDING':
        return <ClockIcon className="w-5 h-5 text-yellow-500" />;
      default:
        return <ClockIcon className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDownloadCSV = async (template) => {
    try {
      const blob = await templateService.downloadTemplateCSV(template.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `template_${template.name}_broadcast.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('CSV template downloaded successfully');
    } catch (error) {
      console.error('Error downloading CSV:', error);
      toast.error('Failed to download CSV template');
    }
  };

  const handleResend = async (template) => {
    if (!window.confirm(t('metaTemplates.confirmResend', 'Are you sure you want to resend this template to Meta for review?'))) {
      return;
    }

    try {
      setResendingTemplateId(template.id);
      const response = await templateService.resendMetaTemplate(template.id);
      if (response.success) {
        toast.success(response.message || t('metaTemplates.resendSuccess', 'Template resent successfully!'));
        if (onRefresh) {
          onRefresh();
        }
      } else {
        toast.error(response.message || t('metaTemplates.resendError', 'Failed to resend template'));
      }
    } catch (error) {
      console.error('Error resending template:', error);
      toast.error(error.response?.data?.detail || t('metaTemplates.resendError', 'Failed to resend template'));
    } finally {
      setResendingTemplateId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Loading templates...</span>
      </div>
    );
  }

  if (templates.length === 0) {
    return (
      <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
        <DocumentArrowDownIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
        <p className="mt-1 text-sm text-gray-500">
          Get started by creating a new template.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('metaTemplates.name', 'Template Name')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('metaTemplates.category', 'Category')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('metaTemplates.language', 'Language')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('metaTemplates.status', 'Status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('metaTemplates.variables', 'Variables')}
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.actions', 'Actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {templates.map((template) => (
              <tr key={template.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => onTemplateClick && onTemplateClick(template)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline text-left"
                  >
                    {template.name}
                  </button>
                  {template.description && (
                    <div className="text-sm text-gray-500">{template.description}</div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900">{template.category}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900">{template.language}</span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {getStatusIcon(template.status)}
                    <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(template.status)}`}>
                      {template.status || 'Unknown'}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="text-sm text-gray-900">
                    {template.variables?.length || 0} {t('metaTemplates.variables', 'variables')}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleDownloadCSV(template)}
                      className="text-blue-600 hover:text-blue-900 p-1"
                      title={t('metaTemplates.downloadCSV', 'Download CSV Template')}
                    >
                      <DocumentArrowDownIcon className="w-5 h-5" />
                    </button>
                    {(template.status === 'PENDING' || template.status === 'REJECTED') && (
                      <button
                        onClick={() => handleResend(template)}
                        disabled={resendingTemplateId === template.id}
                        className="text-yellow-600 hover:text-yellow-900 p-1 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={t('metaTemplates.resend', 'Resend Template')}
                      >
                        {resendingTemplateId === template.id ? (
                          <ArrowPathIcon className="w-5 h-5 animate-spin" />
                        ) : (
                          <ArrowPathIcon className="w-5 h-5" />
                        )}
                      </button>
                    )}
                    {template.status === 'APPROVED' && (
                      <button
                        onClick={() => onBroadcast(template)}
                        className="text-green-600 hover:text-green-900 p-1"
                        title={t('metaTemplates.sendBroadcast', 'Send Broadcast')}
                      >
                        <PaperAirplaneIcon className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MetaTemplateList;

